import { useTranslation } from 'react-i18next';
import { Menu } from '@components/ui/Menu/Menu';
import { CheckIcon, ChevronDownIcon } from '@utils/icons';
import { sortHasDirection, type ColumnSort, type SortDir } from './progressMatrix';
import styles from './sortSelect.module.css';

export interface ISortSelectProps {
  value: ColumnSort;
  onChange: (sort: ColumnSort) => void;
  dir: SortDir;
  onDirChange: (dir: SortDir) => void;
}

const OPTIONS: ColumnSort[] = ['default', 'sequence', 'progress', 'ontime', 'critical'];

/** A down arrow (descending); rotated 180° for ascending via CSS `data-dir`. */
function DirectionArrow() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 5v14M6 13l6 6 6-6" />
    </svg>
  );
}

/**
 * Column ordering control, styled as a D-ORCA filter pill. Three zones:
 *  · the direction arrow (▼ descending / ▲ ascending) — click flips the order;
 *    kept in place but disabled for the neutral sorts, so the pill never reshapes.
 *  · the sort name — click cycles to the NEXT sort. Sized to the widest label
 *    (rendered invisibly behind it) so the pill width never jumps.
 *  · the caret — opens a dropdown to jump straight to any sort (no cycling).
 */
export function SortSelect({ value, onChange, dir, onDirChange }: ISortSelectProps) {
  const { t } = useTranslation();
  const measured = sortHasDirection(value);
  const dirLabel = t(dir === 'desc' ? 'activities.tracker.sort.desc' : 'activities.tracker.sort.asc');
  const cycle = () => onChange(OPTIONS[(OPTIONS.indexOf(value) + 1) % OPTIONS.length]!);
  return (
    <span className={styles.group}>
      <button
        type="button"
        className={styles.dir}
        data-dir={dir}
        disabled={!measured}
        onClick={() => onDirChange(dir === 'desc' ? 'asc' : 'desc')}
        title={measured ? dirLabel : undefined}
        aria-label={measured ? dirLabel : undefined}
      >
        <DirectionArrow />
      </button>
      <button
        type="button"
        className={styles.body}
        onClick={cycle}
        title={t('activities.tracker.sort.next')}
        aria-label={`${t('activities.tracker.sort.label')}: ${t(`activities.tracker.sort.${value}`)}`}
      >
        <span className={styles.nameWrap}>
          {/* Invisible widest-label sizer so cycling never reflows the pill. */}
          {OPTIONS.map((o) => (
            <span key={o} className={styles.ghost} aria-hidden>
              {t(`activities.tracker.sort.${o}`)}
            </span>
          ))}
          <span className={styles.current}>{t(`activities.tracker.sort.${value}`)}</span>
        </span>
      </button>
      <Menu.Root>
        <Menu.Trigger className={styles.caret} aria-label={t('activities.tracker.sort.pick')}>
          <ChevronDownIcon size={14} />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner>
            <Menu.Popup>
              {OPTIONS.map((o) => (
                <Menu.Item key={o} onClick={() => onChange(o)} className={styles.item}>
                  {t(`activities.tracker.sort.${o}`)}
                  {o === value && <CheckIcon size={14} />}
                </Menu.Item>
              ))}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </span>
  );
}
